import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserContext } from "../../../providers/UserProvider";
import { useTeams } from "../../../hooks/useTeams/useTeams";
import { TeamsTable } from "../../components/TeamsTable/TeamsTable";
import { useAccessList } from "../../../hooks/useAccessList/useAccessList";
import { Sidebar } from "../../components/Sidebar/Sidebar";
import { TeamsForm } from "../../components/TeamsForm/TeamsForm";
import { UserGroupWithInstance } from "../../../types/UserGroupWithInstance";
import { useSearchParams } from "react-router-dom";
import { Attention, PaginationControls } from "@optimizely/axiom";

import LimitByRole from "../../components/LimitByRole/LimitByRole";
import { ALL, ATTRIBUTE_ROLES, GROUP_TYPES } from "../../../constants/constants";
import { getUserScopedAttributes } from "../../../lib/scope-check";

import styles from "./TeamsPage.module.scss";
import { ButtonCTA } from "../../components/ButtonCTA/ButtonCTA";

import { deepEquals, getErrorMessage } from "../../../lib/utils";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import { UserGroup } from "../../../domain/UserGroup";
import { emitToast } from "../../../lib/toaster-utils";
import { datadogRum } from "@datadog/browser-rum";
import { ERROR_KEYS } from "../../../constants/data-dog-constants";
import { SearchInput } from "../../components/SearchInput/SearchInput";
import { useDebounce } from "../../../hooks/useDebounce/useDebounce";
import { DEFAULT_DEBOUNCE, MIN_SEARCH_QUERY_LENGTH } from "../../../constants/constants";
import { ResetFilterPrompt } from "../../components/ResetFilterPrompt/ResetFilterPrompt";
import { OrganizationProduct, IOrganizationProductInstance } from "../../../domain/OrganizationProduct";
import { useTeamsEligibleProducts } from "../../../hooks/useTeamsEligibleProducts/useTeamsEligibleProducts";
import { GenericDropdownFilter } from "../../components/GroupFilters/GenericDropdownFilter";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { TableSkeleton } from "../../components/Skeleton/TableSkeleton";
import { TrackingFlow, useAnalyticsTracking } from "../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import { getDeleteTeamPayload } from "../../../lib/analytics-helpers";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";

const TEAM_TYPE_OPTIONS = [
    { id: GROUP_TYPES.INTERNAL, name: "SCIM" },
    { id: GROUP_TYPES.CUSTOM, name: "User-Generated" }
];

export const TeamsPage = ({
    analyticsFlow,
    hideFilters = false,
    instanceId
}: {
    analyticsFlow?: TrackingFlow;
    hideFilters?: boolean;
    instanceId?: string;
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = parseInt(searchParams.get("page") || "1", 10);
    const queryParam = searchParams.get("q") || "";
    const productIdParam = searchParams.get("productId") || "";
    const instanceIdParam = searchParams.get("instanceId") || "";
    const groupTypeParam = searchParams.get("groupType") || "";

    const [selectedTeam, setSelectedTeam] = useState<UserGroupWithInstance | null>(null);
    const [showCreateTeamForm, setShowCreateTeamForm] = useState(false);
    const [teamToBeDeleted, setTeamToBeDeleted] = useState<UserGroupWithInstance | null>(null);
    const [deletionInProgress, setDeletionInProgress] = useState(false);
    const [deletionError, setDeletionError] = useState("");
    const { enabled: enableSCIMTeams } = useFeatureFlag(Flags.ENABLE_SCIM_TEAMS);
    const { enabled: useAttributePermissions } = useFeatureFlag(Flags.USE_ATTRIBUTE_PERMISSIONS);

    const { organizationId, currentInstance } = useUserContext();
    const { sendTrackEvent } = useAnalyticsTracking();

    const {
        debouncedValue: debouncedSearchQuery,
        value: searchQuery,
        setValue: setSearchQuery
    } = useDebounce<string>(queryParam, DEFAULT_DEBOUNCE);

    const [selectedProduct, setSelectedProduct] = useState<OrganizationProduct | null>(null);
    const [selectedInstance, setSelectedInstance] = useState<IOrganizationProductInstance | null>(null);
    const [selectedType, setSelectedType] = useState<string>(ALL);

    const previousSearchWasValidRef = useRef(false);

    const {
        eligibleProducts: availableProducts,
        isLoading: productsLoading,
        error: productsError
    } = useTeamsEligibleProducts({ organizationId });

    const allProductInstances = useMemo(() => selectedProduct?.instances || [], [selectedProduct?.instances]);

    const productIds = useMemo(() => {
        const id = selectedProduct?.id;
        return id ? [id] : [];
    }, [selectedProduct?.id]);

    const accessibleInstanceIds = useMemo(() => {
        if (!useAttributePermissions || !currentInstance?.roles) return undefined;

        const scopedAttributes = getUserScopedAttributes(currentInstance.roles);
        const manageAttr = scopedAttributes[ATTRIBUTE_ROLES.GROUPS.MANAGE];
        const updateAttr = scopedAttributes[ATTRIBUTE_ROLES.GROUPS.UPDATE];

        // If either permission has All/Product scope, user can see all teams
        if (manageAttr?.highestScope === "All" || manageAttr?.highestScope === "Product") return undefined;
        if (updateAttr?.highestScope === "All" || updateAttr?.highestScope === "Product") return undefined;

        // If user has neither permission, don't restrict (they can still view)
        if (!manageAttr && !updateAttr) return undefined;

        // Extract specific instance IDs from scopes
        const ids = new Set<string>();
        [manageAttr, updateAttr].forEach((attr) => {
            attr?.allScopes.forEach((scopeStr) => {
                try {
                    const scope = JSON.parse(scopeStr);
                    if (scope.name === "InstanceId") {
                        ids.add(scope.value);
                    }
                } catch {
                    // skip unparseable scopes (e.g. "[]" for All scope)
                }
            });
        });

        return ids.size > 0 ? Array.from(ids) : undefined;
    }, [useAttributePermissions, currentInstance?.roles]);

    const availableInstances = useMemo(() => {
        if (!accessibleInstanceIds) return allProductInstances;
        const accessibleSet = new Set(accessibleInstanceIds);
        return allProductInstances.filter((inst) => accessibleSet.has(inst.id));
    }, [allProductInstances, accessibleInstanceIds]);

    const instanceIds = useMemo(() => {
        // instanceId prop (from AccessManagementTeams) is always trusted
        if (instanceId) return [instanceId];
        // User-selected instance must be within the accessible set
        if (selectedInstance?.id) {
            const isAvailable = availableInstances.some((inst) => inst.id === selectedInstance.id);
            if (isAvailable) return [selectedInstance.id];
        }
        return accessibleInstanceIds ?? [];
    }, [selectedInstance?.id, instanceId, accessibleInstanceIds, availableInstances]);

    const defaultGroupTypes = useMemo(
        () => (enableSCIMTeams ? [GROUP_TYPES.CUSTOM, GROUP_TYPES.INTERNAL] : [GROUP_TYPES.CUSTOM]),
        [enableSCIMTeams]
    );

    const groupTypesQueryValue = useMemo(
        () => (selectedType === ALL ? defaultGroupTypes : [selectedType]),
        [selectedType, defaultGroupTypes]
    );
    const {
        deleteTeam,
        teams = [],
        pageCount,
        loading,
        error
    } = useTeams({
        groupTypes: groupTypesQueryValue,
        organizationId: organizationId ?? "",
        page,
        query: debouncedSearchQuery.length >= MIN_SEARCH_QUERY_LENGTH ? debouncedSearchQuery : "",
        productIds,
        instanceIds
    });
    const uniqueInstanceIds = useMemo(() => {
        const instanceIds = teams.flatMap((team) => team.instancePermissions.map((ip) => ip.instanceId));
        return Array.from(new Set(instanceIds));
    }, [teams]);

    const { accessList: availableInstanceAccess, isLoading } = useAccessList({
        organizationId,
        instanceIds: uniqueInstanceIds
    });

    const teamsWithProduct = useMemo(() => {
        const instanceMap = new Map(
            (availableInstanceAccess ?? []).flatMap((ai) =>
                ai.instanceAccess.map((ia) => [
                    ia.instance.id,
                    { ...ia.instance, productId: ai.productId, productName: ai.productName }
                ])
            )
        );

        return teams.map((team) => {
            // Optimize: find first matching instance without creating intermediate array
            let foundInstance;
            for (const ip of team.instancePermissions) {
                const inst = instanceMap.get(ip.instanceId);
                if (inst) {
                    foundInstance = inst;
                    break;
                }
            }

            return {
                ...team,
                instance: foundInstance
            };
        });
    }, [teams, availableInstanceAccess]);

    // Sync URL params to state
    useEffect(() => {
        if (!availableProducts.length) return;

        if (productIdParam && !selectedProduct) {
            const product = availableProducts.find((p) => p.id === productIdParam);
            if (product) {
                setSelectedProduct(product);
            }
        }

        if (instanceIdParam && selectedProduct && !selectedInstance) {
            const instance = selectedProduct.instances.find((i) => i.id === instanceIdParam);
            if (instance) {
                setSelectedInstance(instance);
            }
        }

        if (groupTypeParam) {
            const nextType = defaultGroupTypes.includes(groupTypeParam as GROUP_TYPES) ? groupTypeParam : ALL;

            // Only update if different from current state to avoid infinite loop
            setSelectedType((currentType) => {
                return currentType !== nextType ? nextType : currentType;
            });
        } else if (!groupTypeParam) {
            // When no groupType param exists, reset to ALL
            setSelectedType((currentType) => {
                return currentType !== ALL ? ALL : currentType;
            });
        }
    }, [
        availableProducts,
        productIdParam,
        instanceIdParam,
        groupTypeParam,
        selectedProduct,
        selectedInstance,
        defaultGroupTypes
        // Removed selectedType and enableSCIMTeams from dependencies
    ]);

    // Clean up invalid groupType from URL
    useEffect(() => {
        if (!groupTypeParam) return;

        if (!defaultGroupTypes.includes(groupTypeParam as GROUP_TYPES)) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.delete("groupType");
                return newParams;
            });
        }
    }, [groupTypeParam, defaultGroupTypes, setSearchParams]);

    useEffect(() => {
        if (!selectedTeam) return;

        const updatedTeam = teamsWithProduct.find((team) => team.id === selectedTeam.id);
        if (updatedTeam && !deepEquals(selectedTeam, updatedTeam)) {
            setSelectedTeam(updatedTeam);
        }
    }, [teamsWithProduct, selectedTeam]);

    const handleTeamClick = ({ team }: { team: UserGroupWithInstance }) => {
        setSelectedTeam(team);
    };

    const handlePagination = (newPage: number) => {
        setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set("page", `${newPage}`);
            return newParams;
        });
        setSelectedTeam(null);
    };

    const handleFormClose = () => {
        setShowCreateTeamForm(false);
        setSelectedTeam(null);
    };

    const handleProductChange = useCallback(
        ({ value }: { value: OrganizationProduct | IOrganizationProductInstance | null }) => {
            const isProduct = value && "instances" in value;
            const product = isProduct ? (value as OrganizationProduct) : null;

            setSelectedProduct(product);
            setSelectedInstance(null);
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set("page", "1");
                if (product) {
                    newParams.set("productId", product.id);
                } else {
                    newParams.delete("productId");
                }
                newParams.delete("instanceId");
                return newParams;
            });
        },
        [setSearchParams]
    );

    const handleInstanceChange = useCallback(
        ({ value }: { value: OrganizationProduct | IOrganizationProductInstance | null }) => {
            const isInstance = value && "nickname" in value;
            const instance = isInstance ? (value as IOrganizationProductInstance) : null;

            setSelectedInstance(instance);
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set("page", "1");
                if (instance) {
                    newParams.set("instanceId", instance.id);
                } else {
                    newParams.delete("instanceId");
                }
                return newParams;
            });
        },
        [setSearchParams]
    );

    const handleTeamTypeChange = useCallback(
        ({ value }: { value: { id: string } | null }) => {
            setSelectedType(value?.id ?? ALL);
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set("page", "1");
                if (value) {
                    newParams.set("groupType", value.id);
                } else {
                    newParams.delete("groupType");
                }
                return newParams;
            });
        },
        [setSearchParams]
    );

    useEffect(() => {
        const isValidSearch = debouncedSearchQuery.length >= MIN_SEARCH_QUERY_LENGTH;
        const wasValidSearch = previousSearchWasValidRef.current;

        if (isValidSearch || (wasValidSearch && !isValidSearch)) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set("page", "1");
                if (debouncedSearchQuery.length >= MIN_SEARCH_QUERY_LENGTH) {
                    newParams.set("q", debouncedSearchQuery);
                } else {
                    newParams.delete("q");
                }
                return newParams;
            });
        }

        previousSearchWasValidRef.current = isValidSearch;
    }, [debouncedSearchQuery, setSearchParams]);

    const resetFilters = useCallback(() => {
        setSearchQuery("");
        setSelectedProduct(null);
        setSelectedInstance(null);
        setSelectedType(ALL);
        setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set("page", "1");
            newParams.delete("q");
            newParams.delete("productId");
            newParams.delete("instanceId");
            newParams.delete("groupType");
            return newParams;
        });
    }, [setSearchQuery, setSearchParams]);

    const hasActiveFilters = useMemo(() => {
        return (
            debouncedSearchQuery.length >= MIN_SEARCH_QUERY_LENGTH ||
            selectedProduct !== null ||
            selectedInstance !== null ||
            selectedType !== ALL
        );
    }, [debouncedSearchQuery, selectedProduct, selectedInstance, selectedType]);

    const handleTeamDeletion = () => {
        const originalTeamsLength = teams.length;
        setDeletionInProgress(true);
        setDeletionError("");
        deleteTeam({ team: teamToBeDeleted as UserGroup })
            .then(() => {
                if (selectedTeam && teamToBeDeleted && selectedTeam.id === teamToBeDeleted.id) {
                    setSelectedTeam(null);
                }
                const shouldResetPage = originalTeamsLength === 1 && page > 1;
                if (shouldResetPage) {
                    setSearchParams((prevParams) => {
                        const newParams = new URLSearchParams(prevParams);
                        newParams.set("page", `${page - 1}`);
                        return newParams;
                    });
                }
                const deletionAnalyticsPayload = getDeleteTeamPayload({
                    flow: analyticsFlow,
                    instanceId: teamToBeDeleted?.instance?.id ?? "",
                    productId: teamToBeDeleted?.instance?.productId ?? ""
                });
                sendTrackEvent(deletionAnalyticsPayload);
                setTeamToBeDeleted(null);
                emitToast({ message: "Team deleted successfully." });
            })
            .catch((e) => {
                const errorMessage = getErrorMessage(e);
                setDeletionError(errorMessage);
                datadogRum.addError(new Error(ERROR_KEYS.TEAMS.FAILED_TO_DELETE), {
                    teamId: teamToBeDeleted?.id ?? "unknown",
                    errorKey: ERROR_KEYS.TEAMS.FAILED_TO_DELETE,
                    message: errorMessage,
                    source: "TeamsPage.handleTeamDeletion"
                });
            })
            .finally(() => {
                setDeletionInProgress(false);
            });
    };

    const isLoadingTable = loading || Boolean(uniqueInstanceIds.length > 0 && isLoading);
    const showNoResults = !loading && teams.length === 0 && hasActiveFilters;
    const showEmptyState = !loading && teams.length === 0 && !hasActiveFilters;
    return (
        <div className={styles["teams-page"]}>
            <div className={styles["teams-page__action-bar"]}>
                <div className={styles["teams-page__filter-bar"]}>
                    <div className={styles["teams-page__search-input"]}>
                        <SearchInput
                            placeholder="Search teams by name..."
                            type="text"
                            value={searchQuery}
                            onChange={({ value }) => setSearchQuery(value)}
                            ariaLabel="Search teams by name"
                        />
                    </div>
                    {!hideFilters && (
                        <>
                            {productsLoading ? (
                                <>
                                    {enableSCIMTeams && (
                                        <Skeleton width="150px" height="40px" data-cy="teams-type-filter-skeleton" />
                                    )}
                                    <Skeleton width="150px" height="40px" data-cy="teams-product-filter-skeleton" />
                                    <Skeleton width="150px" height="40px" data-cy="teams-instance-filter-skeleton" />
                                </>
                            ) : (
                                <>
                                    {enableSCIMTeams && (
                                        <GenericDropdownFilter
                                            data-cy="teams-type-filter"
                                            label="Team Types"
                                            options={TEAM_TYPE_OPTIONS}
                                            value={
                                                TEAM_TYPE_OPTIONS.find((option) => option.id === selectedType) || null
                                            }
                                            onChange={handleTeamTypeChange}
                                            disabled={!!productsError}
                                        />
                                    )}
                                    <GenericDropdownFilter
                                        data-cy="teams-product-filter"
                                        label={productsError ? "Error loading products" : "Product"}
                                        options={availableProducts}
                                        value={selectedProduct}
                                        onChange={handleProductChange}
                                        disabled={!!productsError}
                                    />
                                    <GenericDropdownFilter
                                        data-cy="teams-instance-filter"
                                        label="Instance"
                                        options={availableInstances}
                                        value={selectedInstance}
                                        onChange={handleInstanceChange}
                                        disabled={!selectedProduct}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>

                <LimitByRole action={ATTRIBUTE_ROLES.GROUPS.CREATE} mode="hide">
                    <div className={styles["teams-page__bar"]}>
                        <div className={styles["teams-page__create-team"]}>
                            <ButtonCTA onClick={() => setShowCreateTeamForm(true)} actionText="Create A Team..." />
                        </div>
                    </div>
                </LimitByRole>
            </div>

            {loading && teams.length === 0 && !showEmptyState && (
                <TableSkeleton fullWidth data-cy="teams-loading-skeleton" />
            )}

            {showEmptyState && (
                <div data-cy="teams-empty-state">
                    <p>No teams have been created yet.</p>
                </div>
            )}

            {showNoResults && (
                <ResetFilterPrompt
                    data-cy="teams-reset-filters"
                    data-testid="teams-reset-filters"
                    onClick={resetFilters}
                    prompt="No teams match your search or filter criteria."
                />
            )}

            {productsError && (
                <Attention data-cy="teams-products-error" data-testid="teams-products-error" type="bad-news">
                    Unable to load product information. Please refresh the page or contact support if the issue
                    persists.
                </Attention>
            )}

            {teams.length > 0 && (
                <>
                    <TeamsTable
                        showSCIMBadge={enableSCIMTeams}
                        selectedTeam={selectedTeam}
                        onTeamClick={handleTeamClick}
                        teams={teamsWithProduct}
                        onTeamDelete={({ team }) => setTeamToBeDeleted(team)}
                        isLoading={isLoadingTable}
                        error={Boolean(error)}
                    />
                    {pageCount > 1 && (
                        <PaginationControls
                            className="anchor--bottom"
                            currentPage={page}
                            goToPage={handlePagination}
                            totalPages={pageCount}
                        />
                    )}
                </>
            )}

            {!!teamToBeDeleted && (
                <ConfirmationDialog
                    onCancel={() => setTeamToBeDeleted(null)}
                    onConfirmation={handleTeamDeletion}
                    loading={deletionInProgress}
                    title="Delete Team"
                    error={deletionError}
                >
                    Are you sure you want to delete team <span className="weight--bold">{teamToBeDeleted.name}</span>?
                    This action cannot be undone.
                </ConfirmationDialog>
            )}
            {(!!selectedTeam || showCreateTeamForm) && (
                <Sidebar heading="Team" isOpen={true} onCancel={handleFormClose}>
                    <TeamsForm
                        analyticsFlow={analyticsFlow}
                        instanceId={instanceId}
                        team={selectedTeam ?? undefined}
                        onClose={handleFormClose}
                    />
                </Sidebar>
            )}
        </div>
    );
};
